<script>
import ViewSessionDetailModal from '@/V2/collect/ViewSessionDetailModal';
import {
  iBadge,
  iBadgeColours,
  iButton,
  iPagination,
  iTable,
  iTableRow,
} from '@inscribe/signal';
import { mapGetters, mapState, mapActions } from 'vuex';
import { formatDateTime } from '@/utils/date';
import IconText from '@/V2/components/IconText.vue';
import EmptyPage from '@/V2/components/EmptyPage';
import PageHeader from '@/V2/components/PageHeader.vue';

export default {
  components: {
    EmptyPage,
    iBadge,
    iButton,
    IconText,
    iPagination,
    iTable,
    iTableRow,
    ViewSessionDetailModal,
    PageHeader,
  },
  data() {
    return {
      iBadgeColours,
      show: false,
      selectedSession: null,
    };
  },
  computed: {
    ...mapState({
      collectSessions: 'collectSessions',
    }),

    currentPage() {
      return parseInt(this.$route?.query?.page) || 1;
    },

    ...mapGetters({
      collectEnabled: 'collectEnabled',
    }),

    isShowable() {
      return this.show && !!this.selectedSession;
    },
  },

  watch: {
    '$route.query.page': {
      immediate: true,
      handler: function () {
        this.fetchCollectSessions();
      },
    },
  },

  methods: {
    ...mapActions({
      getSessions: 'getSessions',
    }),

    fetchCollectSessions() {
      this.getSessions({
        page: this.$route.query.page || 1,
      });
    },

    changePage(newPage) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: newPage,
        },
      });
    },

    customizeRedirect() {
      this.$router.push({
        name: 'collect-config-settings-view',
      });
    },

    showPanel(session) {
      this.show = true;
      this.selectedSession = session;
    },

    hidePanel() {
      this.show = false;
      this.selectedSession = null;
    },

    requestLabel(requests, label) {
      return requests && requests.length === 1
        ? `${requests.length} ${label}`
        : `${requests.length} ${label}s`;
    },

    formatDateTime,

    sessionIsLiveCapture(session) {
      return session.document_requests.some((request) =>
        Boolean(request.live_capture_mode),
      );
    },
  },
};
</script>

<template>
  <div class="collect-sessions-list d-flex flex-column w-100">
    <PageHeader title="Collect Sessions">
      <template #primary-actions>
        <i-button
          v-if="collectEnabled"
          text="Customize"
          @button-click="customizeRedirect()"
          v-read-only-check="{
            message: 'Customize is not accessible for read-only users',
          }"
        ></i-button>
      </template>
    </PageHeader>

    <view-session-detail-modal
      v-if="selectedSession"
      :selected-session="selectedSession"
      :is-showable="isShowable"
      @hide-view-modal="hidePanel"
    />

    <div
      class="d-flex flex-column align-items-center collect-sessions-list__wrapper i-px-12 i-py-11"
    >
      <div class="w-100 h-100 i-mb-2 i-mt-2">
        <!-- empty state -->
        <empty-page
          v-if="!collectSessions.loading && !collectSessions.list.length"
          title="No sessions have been created so far"
          subtitle="Create a new session using the 'Collect' button in any
            customer."
          icon="medium-file-request"
        />

        <!-- table with data -->
        <i-table v-else :loading="collectSessions.loading" class="users-table">
          <!-- table header -->
          <template #header>
            <i-table-row type="header">
              <template #cells>
                <div class="no-padding-x">Session</div>
                <div>Status</div>
                <div>Live Image Capture</div>
                <div>Date Created</div>
              </template>
            </i-table-row>
          </template>

          <!-- table body -->
          <template #rows>
            <i-table-row
              v-for="session of collectSessions.list"
              :key="session.id"
              class="high"
              @on-row-click="showPanel(session)"
            >
              <template #cells>
                <!-- data -->
                <div class="no-padding-x">
                  <icon-text
                    data-dd-action-name="collect-sessions-list-session-link"
                    icon="medium-file-request"
                    class="i-py-4"
                    :title="session.customer_name"
                    :subtitle="
                      session.customer_information_requests
                        ? requestLabel(
                            session.customer_information_requests,
                            'Information Request',
                          )
                        : requestLabel(
                            session.document_requests,
                            'Document Request',
                          )
                    "
                  ></icon-text>
                </div>

                <!-- status -->
                <div class="status centered">
                  <i-badge
                    :colour="
                      session.completed_at
                        ? iBadgeColours.SUCCESS
                        : session.expired
                          ? iBadgeColours.WARNING
                          : iBadgeColours.NEUTRAL
                    "
                    :label="
                      session.completed_at
                        ? 'Completed'
                        : session.expired
                          ? 'Expired'
                          : 'Pending'
                    "
                    :title="
                      session.completed_at
                        ? formatDateTime(session.completed_at, 'lll')
                        : undefined
                    "
                  ></i-badge>
                </div>

                <!-- Live Image Capture   -->
                <div class="centered">
                  <i-badge
                    :label="
                      sessionIsLiveCapture(session) ? 'Enabled' : 'Disabled'
                    "
                    :colour="iBadgeColours.NEUTRAL"
                    :outline="!sessionIsLiveCapture(session)"
                  ></i-badge>
                </div>

                <div class="centered">
                  <span class="i-text-body-2 i-text-neutral-110">{{
                    formatDateTime(session.created_at, 'lll')
                  }}</span>
                </div>
              </template>
            </i-table-row>
          </template>
        </i-table>

        <div
          v-if="!collectSessions.loading && collectSessions.pages > 1"
          class="i-mt-12 i-mb-10"
        >
          <i-pagination
            :total-items="collectSessions.total"
            :items-per-page="10"
            :selected-page="currentPage"
            @page-click="changePage"
          ></i-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.collect-session-info-list {
  list-style-position: inside;
  padding-left: 0;

  li {
    list-style-type: none;
    padding: 0;
    margin: 0;

    &:not(:first-child)::before {
      content: '\2022';
      margin-left: 6px;
      margin-right: 1px;
    }
  }
}
</style>

<style lang="scss" scoped>
.collect-sessions-list {
  &__wrapper {
    height: 100%;
  }

  .i-badge {
    width: fit-content;
  }
}
</style>
